// Mock data shaped like the real /connectors/registry and
// /connectors/credentials/status/all backend responses.
// Used by the Architecture demo's readonly ConnectorsTab.

export const ENABLED_CONNECTORS = [
  'quickbooks',
  'active_campaign',
  'calendly',
  'hubspot',
  'google_analytics',
  'google_search_console',
  'grain',
  'zoom',
  'clickup',
  'semrush',
];

// What the backend /connectors/registry endpoint returns.
export const CONNECTOR_REGISTRY = {
  quickbooks: {
    domain: 'Finance',
    auth_type: 'oauth2',
    credential_fields: [
      { key: 'client_id', label: 'QB Client ID', required: true },
      { key: 'client_secret', label: 'QB Client Secret', required: true },
      { key: 'realm_id', label: 'Company ID (Realm)', required: true },
      { key: 'redirect_uri', label: 'Redirect URI', required: true },
    ],
  },
  active_campaign: {
    domain: 'Marketing',
    auth_type: 'api_key_header',
    credential_fields: [
      { key: 'api_key', label: 'API Key', required: true },
      { key: 'base_url', label: 'Base URL', required: false },
    ],
  },
  calendly: {
    domain: 'Sales',
    auth_type: 'bearer_token',
    credential_fields: [
      { key: 'api_key', label: 'Personal Access Token', required: true },
    ],
  },
  hubspot: {
    domain: 'Sales',
    auth_type: 'bearer_token',
    credential_fields: [
      { key: 'api_key', label: 'Private App Token', required: true },
    ],
  },
  google_analytics: {
    domain: 'Marketing',
    auth_type: 'service_account',
    credential_fields: [
      { key: 'service_account_json', label: 'Service Account JSON', required: true },
      { key: 'property_id', label: 'GA4 Property ID', required: true },
    ],
  },
  google_search_console: {
    domain: 'Marketing',
    auth_type: 'service_account',
    credential_fields: [
      { key: 'service_account_json', label: 'Service Account JSON', required: false },
      { key: 'site_url', label: 'Site URL', required: true },
    ],
  },
  grain: {
    domain: 'Sales',
    auth_type: 'api_key_header',
    credential_fields: [
      { key: 'api_token', label: 'Grain API Token', required: true },
      { key: 'api_version', label: 'API Version', required: false },
    ],
  },
  zoom: {
    domain: 'Sales',
    auth_type: 'service_account',
    credential_fields: [
      { key: 'account_id', label: 'Zoom Account ID', required: true },
      { key: 'client_id', label: 'Zoom Client ID', required: true },
      { key: 'client_secret', label: 'Zoom Client Secret', required: true },
    ],
  },
  clickup: {
    domain: 'Operations',
    auth_type: 'api_key_header',
    credential_fields: [
      { key: 'api_key', label: 'API Key', required: true },
    ],
  },
  semrush: {
    domain: 'Marketing',
    auth_type: 'api_key_header',
    credential_fields: [
      { key: 'api_key', label: 'Semrush API Key', required: true },
      { key: 'domains', label: 'Domains', required: true },
      { key: 'database', label: 'Database region', required: false },
    ],
  },
};

const cred = (masked_value, daysAgo = 14) => ({
  masked_value,
  updated_at: new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString(),
});

// What the backend /connectors/credentials/status/all endpoint returns.
export const CONNECTOR_STATUSES = {
  quickbooks: {
    connected: true,
    has_credentials: true,
    credential_keys: ['client_id', 'client_secret', 'realm_id', 'redirect_uri', 'access_token', 'refresh_token'],
    credentials: {
      client_id: cred('ABwGW...x7Qe', 42),
      client_secret: cred('•••••••••••', 42),
      realm_id: cred('9341 4541 8839 2017', 42),
      redirect_uri: cred('https://app.scaletime.co/qb-callback', 42),
      access_token: cred('eyJlbm...c2VydA', 0),
      refresh_token: cred('AB11738...UV93', 14),
    },
  },
  active_campaign: {
    connected: true,
    has_credentials: true,
    credential_keys: ['api_key', 'base_url'],
    credentials: {
      api_key: cred('a7f3...9c21', 30),
      base_url: cred('https://scaletime.api-us1.com', 30),
    },
  },
  calendly: {
    connected: true,
    has_credentials: true,
    credential_keys: ['api_key'],
    credentials: {
      api_key: cred('eyJraWQ...A7gz', 7),
    },
  },
  hubspot: {
    connected: true,
    has_credentials: true,
    credential_keys: ['api_key'],
    credentials: {
      api_key: cred('pat-na1-...8f24', 21),
    },
  },
  google_analytics: {
    connected: true,
    has_credentials: true,
    credential_keys: ['service_account_json', 'property_id'],
    credentials: {
      service_account_json: cred('{ "type": "service_account", ... }', 60),
      property_id: cred('properties/316919056', 60),
    },
  },
  google_search_console: {
    connected: true,
    has_credentials: true,
    credential_keys: ['site_url'],
    credentials: {
      site_url: cred('sc-domain:scaletime.co', 60),
    },
  },
  grain: {
    connected: false,
    has_credentials: false,
    credential_keys: [],
    credentials: {},
  },
  zoom: {
    connected: false,
    has_credentials: false,
    credential_keys: [],
    credentials: {},
  },
  clickup: {
    connected: true,
    has_credentials: true,
    credential_keys: ['api_key'],
    credentials: {
      api_key: cred('pk_8472...ZE9R', 4),
    },
  },
  semrush: {
    connected: false,
    has_credentials: false,
    credential_keys: [],
    credentials: {},
  },
};
